/**
 * Оценка аптеки после заказа: что спрашиваем и о каком заказе.
 *
 * Пока у бэкенда нет ручек, ожидающие оценки mock выбирает из закрытых
 * заказов, которые приложение уже получило от API (`GET /orders`). Здесь —
 * как из заказа получается такой кандидат.
 */
import type { PendingReviewDto } from '@dorixona/api/reviews'
import { isoString } from '@dorixona/utils'
import type { Order } from '../account/api'
import { isOrderClosed } from '../account/model'

/** Звёзд в оценке. */
export const STARS = [1, 2, 3, 4, 5] as const

/** О каком заказе спрашиваем: номер и аптека — в вопросе шторки. */
export interface ReviewTarget {
  orderId: number
  orderNumber: number
  storeName: string
}

export function toTarget(dto: PendingReviewDto): ReviewTarget {
  return { orderId: dto.order_id, orderNumber: dto.order_number, storeName: dto.store_name }
}

export function orderTarget(order: Order): ReviewTarget {
  return { orderId: order.id, orderNumber: order.number, storeName: order.storeName }
}

/**
 * Когда заказ закрыли: время события, которым он стал выданным или
 * доставленным. Такого события в ответе нет (список заказов может приходить
 * без истории) — время оформления: раньше закрытия оно быть не может, так что
 * заказ скорее выпадет из окна, чем застрянет в нём.
 */
export function closedAt(order: Order): number {
  const at = order.events.reduce(
    (last, e) => (e.status === order.status ? Math.max(last, e.at) : last),
    0,
  )
  return at || order.createdAt
}

/** Закрытые заказы покупателя — кандидаты в оценку для mock. */
export function closedCandidates(orders: Order[]): PendingReviewDto[] {
  return orders
    .filter((order) => isOrderClosed(order.status))
    .map((order) => ({
      order_id: order.id,
      order_number: order.number,
      store_id: order.storeId,
      store_name: order.storeName,
      closed_at: isoString(closedAt(order)),
    }))
}
